import type { Run } from "@/types/chemistry";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Plus, Calendar, FlaskConical } from "lucide-react";
import { formatDate } from "@/lib/formatDate";
import { ScrollArea } from "@/components/ui/scroll-area";

interface RunsTabProps {
  projectId: string;
  runs: Run[];
  onNewRun: () => void;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  draft: { label: "Draft", variant: "outline" },
  configured: { label: "Configured", variant: "secondary" },
  ready: { label: "Ready", variant: "secondary" },
  in_progress: { label: "In Progress", variant: "default" },
  complete: { label: "Complete", variant: "secondary" },
};

export const RunsTab = ({ projectId, runs, onNewRun }: RunsTabProps) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 px-4 pt-4 pb-2">
        {runs.length === 0 ? (
          <p className="text-xs text-muted-foreground/60 text-center py-6 italic">
            No runs yet
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {runs.map((run) => {
              const cfg = statusConfig[run.status] ?? statusConfig.draft;
              return (
                <button
                  key={run.id}
                  onClick={() => navigate(`/project/${projectId}/run/${run.id}`)}
                  className="w-full text-left border border-border rounded-md px-3 py-2.5 hover:border-primary/50 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground truncate pr-2">
                      {run.title}
                    </span>
                    <Badge variant={cfg.variant} className="shrink-0 text-[10px]">
                      {cfg.label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <FlaskConical className="h-3 w-3" />
                      <span>{run.selectedStepIds.length} steps</span>
                    </div>
                    {run.deadline && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(run.deadline)}</span>
                      </div>
                    )}
                    <span className="ml-auto text-muted-foreground">
                      edited {new Date(run.updatedAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* New Run button — subtle, at the bottom */}
      <div className="px-4 py-3 border-t border-border">
        <button
          onClick={onNewRun}
          className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors py-1"
        >
          <Plus className="h-3 w-3" />
          New Run
        </button>
      </div>
    </div>
  );
};
